import type { View } from "@novorender/api";
import { quat, vec2, vec3 } from "gl-matrix";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { ELEMENT_IDS } from "features/bookmarks/utils";
import type { useClippingPlaneActions } from "features/clippingPlanes/useClippingPlaneActions";
import { getClippingPlaneAnchorId } from "features/clippingPlanes/useClippingPlaneActions";
import { getCameraDir } from "features/engine2D/utils";
import type { CameraState, RenderState } from "features/render";
import { CameraType, selectCameraType } from "features/render";
import { getRayLineClosestParam } from "utils/math";

import type { useSectionPlaneUndoRedo } from "../sectionPlaneUndoRedo";
import { buildPerspectiveRayDir, getGizmoScreenPos, getScreenSpaceMoveDirAndMult } from "../sectionPlaneUtils";

const TOUCH_DRAG_THRESHOLD_PX = 10;
const MOUSE_DRAG_THRESHOLD_PX = 4;

function snapshotOrthoCameraState(view: View): CameraState | null {
    const cam = view.renderState.camera;
    if (cam.kind !== "orthographic") {
        return null;
    }
    return {
        type: CameraType.Orthographic,
        goTo: {
            position: vec3.clone(cam.position),
            rotation: quat.clone(cam.rotation),
            far: cam.far,
        },
    };
}

function createDragOverlay(): HTMLDivElement {
    const overlay = document.createElement("div");
    overlay.style.setProperty("position", "absolute");
    overlay.style.setProperty("left", "0px");
    overlay.style.setProperty("top", "0px");
    overlay.style.setProperty("width", "100%");
    overlay.style.setProperty("height", "100%");
    overlay.style.setProperty("z-index", "10000");
    document.body.appendChild(overlay);
    return overlay;
}

function hideSvgCursors(): () => void {
    const cursor = document.getElementById("cursor");
    const cameraCursor = document.getElementById("camera-cursor");

    if (cursor) {
        cursor.innerHTML = "";
        cursor.style.display = "none";
    }
    if (cameraCursor) {
        cameraCursor.innerHTML = "";
        cameraCursor.style.display = "none";
    }

    return () => {
        if (cursor) {
            cursor.style.display = "";
        }
        if (cameraCursor) {
            cameraCursor.style.display = "";
        }
    };
}

type RayLineScratch = { crossDN: vec3; diff: vec3; crossDDiff: vec3 };

function tryInitRayMode(
    view: View,
    canvas: HTMLCanvasElement,
    originalAnchorPos: vec3,
    normal: vec3,
    clientX: number,
    clientY: number,
    rayDirScratch: vec3,
    rayLineScratch: RayLineScratch,
):
    | { useRayMode: true; initialT: number; buildRayDir: (x: number, y: number, out: vec3) => void }
    | { useRayMode: false } {
    const camera = view.renderState.camera;
    if (camera.kind === "orthographic") {
        return { useRayMode: false };
    }

    const canvasRect = canvas.getBoundingClientRect();
    if (canvasRect.width <= 0 || canvasRect.height <= 0 || camera.fov <= 0) {
        return { useRayMode: false };
    }

    const cameraDir = getCameraDir(camera.rotation);
    const buildRayDir = buildPerspectiveRayDir(camera.rotation, cameraDir, camera.fov, canvasRect);
    buildRayDir(clientX, clientY, rayDirScratch);
    const t = getRayLineClosestParam(camera.position, rayDirScratch, originalAnchorPos, normal, rayLineScratch);

    if (t === null) {
        return { useRayMode: false };
    }

    return { useRayMode: true, initialT: t, buildRayDir };
}

function initScreenSpaceDirection(
    view: View,
    anchorPos: vec3,
    normal: vec3,
    screenMoveDir: vec2,
    refPosScratch: vec3,
): { mult: number } | null {
    const result = getScreenSpaceMoveDirAndMult(view, anchorPos, normal, screenMoveDir, refPosScratch);
    return result ? { mult: result.mult } : null;
}

export function useSectionPlaneDrag({
    view,
    clipping,
    actions,
    undoRedo,
    onTapPlane,
    getDisplayAnchorPos,
    onDragStateChange,
}: {
    view?: View;
    clipping: RenderState["clipping"];
    actions: Pick<ReturnType<typeof useClippingPlaneActions>, "movePlanes">;
    undoRedo: Pick<ReturnType<typeof useSectionPlaneUndoRedo>, "snapshotPlanes" | "finishPlaneOffsetWithUndo">;
    onTapPlane?: (index: number, screenPoint: vec2) => void;

    /** Returns the gizmo's current displayed 3D position for the given plane index, or null. */
    getDisplayAnchorPos?: (index: number) => vec3 | null;

    /** Signals drag start/stop so the gizmo DOM can show/clear drag feedback. */
    onDragStateChange?: (index: number, dragging: boolean) => void;
}) {
    const cameraType = useAppSelector(selectCameraType);
    const [movingPlaneIndex, setMovingPlaneIndex] = useState(-1);
    const cleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        return () => {
            cleanupRef.current?.();
        };
    }, []);

    const handlePointerDown = useCallback(
        (e: ReactPointerEvent, index: number) => {
            if (!view || e.button !== 0) {
                return;
            }

            const plane = clipping.planes[index];
            if (!plane?.anchorPos) {
                return;
            }
            const originalAnchorPos = plane.anchorPos;
            const originalNormalOffset = plane.normalOffset;

            let effectiveAnchorPos: vec3 = vec3.clone(originalAnchorPos);
            let effectiveNormalOffsetW = originalNormalOffset[3];
            if (getDisplayAnchorPos) {
                const displayPos = getDisplayAnchorPos(index);
                if (displayPos) {
                    const [nx, ny, nz] = originalNormalOffset;
                    effectiveAnchorPos = vec3.clone(displayPos);
                    effectiveNormalOffsetW = nx * displayPos[0] + ny * displayPos[1] + nz * displayPos[2];
                }
            }

            const startDrag = (
                startClientX: number,
                startClientY: number,
                initialMoveEvent?: PointerEvent,
                activePointerId?: number,
            ) => {
                const restoreSvgCursors = hideSvgCursors();
                const overlay = createDragOverlay();
                overlay.style.cursor = "grabbing";
                setMovingPlaneIndex(index);
                onDragStateChange?.(index, true);

                const state = actions.movePlanes(view, clipping.planes, [index]);
                const planesBeforeDrag = undoRedo.snapshotPlanes();

                const normal = vec3.fromValues(
                    originalNormalOffset[0],
                    originalNormalOffset[1],
                    originalNormalOffset[2],
                );
                const cameraDir = getCameraDir(view.renderState.camera.rotation);
                const isFacingNormal = Math.abs(vec3.dot(normal, cameraDir)) > 0.999;

                const cameraBefore =
                    cameraType === CameraType.Orthographic && isFacingNormal ? snapshotOrthoCameraState(view) : null;

                const rayDirScratch = vec3.create();
                const refPosScratch = vec3.create();
                const computedAnchorScratch = vec3.create();
                const rayLineScratch: RayLineScratch = {
                    crossDN: vec3.create(),
                    diff: vec3.create(),
                    crossDDiff: vec3.create(),
                };
                const screenMoveDir = vec2.create();
                const pointerPos = vec2.create();
                const pointerDelta = vec2.create();
                const lastPointerPos = vec2.fromValues(startClientX, startClientY);

                const canvas = document.getElementById(ELEMENT_IDS.mainCanvas) as HTMLCanvasElement | null;

                let distanceBasedMult = 1;
                let useRayMode = false;
                let initialT = 0;
                let buildRayDir: ((clientX: number, clientY: number, out: vec3) => void) | null = null;

                // If the plane is off-screen (its anchor projects behind the camera or outside
                // the viewport), the gizmo is only a parked edge indicator.
                // Intersect the grab ray with the current plane:
                //  • If the hit is in front of the camera, the cut IS visible - just
                //    relocate the anchor to the hit point so the drag reference is
                //    under the cursor. The plane stays put.
                //  • If the hit is behind the camera (or the ray is parallel), the cut
                //    is truly invisible - teleport the plane forward by mirroring the
                //    depth so it reappears at a sensible distance.
                const cameraPos = view.renderState.camera.position;
                if (canvas && view.renderState.camera.kind === "pinhole" && view.renderState.camera.fov > 0) {
                    const canvasRect = canvas.getBoundingClientRect();
                    const [anchorScreen] = view.convert.worldSpaceToScreenSpace([originalAnchorPos], { round: false });
                    const anchorOffScreen =
                        !anchorScreen ||
                        anchorScreen[0] < 0 ||
                        anchorScreen[0] > canvasRect.width ||
                        anchorScreen[1] < 0 ||
                        anchorScreen[1] > canvasRect.height;
                    if (anchorOffScreen && canvasRect.width > 0 && canvasRect.height > 0) {
                        const teleportDir = vec3.create();
                        buildPerspectiveRayDir(
                            view.renderState.camera.rotation,
                            cameraDir,
                            view.renderState.camera.fov,
                            canvasRect,
                        )(startClientX, startClientY, teleportDir);

                        const grabDenom = vec3.dot(normal, teleportDir);
                        const planeNumerator = originalNormalOffset[3] - vec3.dot(normal, cameraPos);

                        if (Math.abs(grabDenom) > 1e-6) {
                            const tGrab = planeNumerator / grabDenom;
                            if (tGrab > 0) {
                                // Plane is in front of the camera along the grab ray.
                                // Just move the anchor onto the plane under the cursor.
                                effectiveAnchorPos = vec3.scaleAndAdd(vec3.create(), cameraPos, teleportDir, tGrab);
                                // effectiveNormalOffsetW stays at originalNormalOffset[3]
                            } else {
                                // Plane is behind the camera - mirror to bring it forward.
                                const dist = Math.max(1, Math.abs(tGrab));
                                effectiveAnchorPos = vec3.scaleAndAdd(vec3.create(), cameraPos, teleportDir, dist);
                                effectiveNormalOffsetW = vec3.dot(normal, effectiveAnchorPos);
                            }
                        } else {
                            // Grab ray is nearly parallel to the plane.
                            // Fall back to the camera-to-plane depth along the view direction.
                            const centerDenom = vec3.dot(normal, cameraDir);
                            const dist =
                                Math.abs(centerDenom) > 1e-6
                                    ? Math.max(1, Math.abs(planeNumerator / centerDenom))
                                    : Math.max(1, vec3.dist(cameraPos, originalAnchorPos));
                            effectiveAnchorPos = vec3.scaleAndAdd(vec3.create(), cameraPos, teleportDir, dist);
                            effectiveNormalOffsetW = vec3.dot(normal, effectiveAnchorPos);
                        }
                    }
                }

                if (isFacingNormal) {
                    vec2.set(screenMoveDir, 0, 1);
                } else if (canvas) {
                    const rayResult = tryInitRayMode(
                        view,
                        canvas,
                        effectiveAnchorPos,
                        normal,
                        startClientX,
                        startClientY,
                        rayDirScratch,
                        rayLineScratch,
                    );
                    if (rayResult.useRayMode) {
                        useRayMode = true;
                        initialT = rayResult.initialT;
                        buildRayDir = rayResult.buildRayDir;
                    }
                }

                // Look up the gizmo DOM element so we can keep it positioned
                // at the effective anchor during drag (movePlanes.update always
                // computes from the Redux anchor, which may be off-screen).
                const anchorEl = document.querySelector(`#${getClippingPlaneAnchorId(index)}`) as HTMLElement | null;
                const needsGizmoOverride = anchorEl && !vec3.equals(effectiveAnchorPos, originalAnchorPos);

                /** Update the plane offset AND keep the gizmo near the effective anchor. */
                function applyOffset(offset: number) {
                    state.update([offset]);
                    if (needsGizmoOverride && view) {
                        vec3.scaleAndAdd(
                            computedAnchorScratch,
                            effectiveAnchorPos,
                            normal,
                            offset - effectiveNormalOffsetW,
                        );
                        const pos2d =
                            canvas && canvas.clientWidth > 0 && canvas.clientHeight > 0
                                ? getGizmoScreenPos(
                                      view,
                                      computedAnchorScratch,
                                      canvas.clientWidth,
                                      canvas.clientHeight,
                                  )
                                : view.convert.worldSpaceToScreenSpace([computedAnchorScratch], { round: true })[0];
                        if (pos2d) {
                            const x = canvas ? Math.max(0, Math.min(canvas.clientWidth, pos2d[0])) : pos2d[0];
                            const y = canvas ? Math.max(0, Math.min(canvas.clientHeight, pos2d[1])) : pos2d[1];
                            anchorEl!.style.setProperty("left", `${x}px`);
                            anchorEl!.style.setProperty("top", `${y}px`);
                        }
                    }
                }

                let stopped = false;
                let newOffset = effectiveNormalOffsetW;
                let lastComputedOffset = newOffset;

                // Teleport the plane to the gizmo's display position at drag start
                if (effectiveNormalOffsetW !== originalNormalOffset[3]) {
                    applyOffset(effectiveNormalOffsetW);
                }

                const stop = (save: boolean) => {
                    if (stopped) {
                        return;
                    }
                    stopped = true;
                    window.removeEventListener("keydown", onKeyDown);
                    window.removeEventListener("pointermove", onPointerMove);
                    window.removeEventListener("pointerup", onPointerUp);
                    window.removeEventListener("pointercancel", onPointerCancel);
                    overlay.remove();
                    restoreSvgCursors();
                    cleanupRef.current = null;
                    setMovingPlaneIndex(-1);
                    onDragStateChange?.(index, false);
                    state.finish(save);

                    if (save && view) {
                        const cameraAfter = cameraBefore ? snapshotOrthoCameraState(view) : null;
                        undoRedo.finishPlaneOffsetWithUndo(
                            planesBeforeDrag,
                            index,
                            cameraBefore && cameraAfter ? { before: cameraBefore, after: cameraAfter } : undefined,
                        );
                    }
                };

                if (!isFacingNormal && !useRayMode) {
                    const screenResult = initScreenSpaceDirection(
                        view,
                        effectiveAnchorPos,
                        normal,
                        screenMoveDir,
                        refPosScratch,
                    );
                    if (!screenResult) {
                        stop(false);
                        return;
                    }
                    distanceBasedMult = screenResult.mult;
                }

                function onKeyDown(event: KeyboardEvent) {
                    if (event.key === "Escape") {
                        stop(false);
                    }
                }

                function onPointerCancel(event: PointerEvent) {
                    if (activePointerId !== undefined && event.pointerId !== activePointerId) {
                        return;
                    }
                    stop(false);
                }

                function onPointerUp(event: PointerEvent) {
                    if (activePointerId !== undefined && event.pointerId !== activePointerId) {
                        return;
                    }
                    stop(true);
                }

                function onPointerMove(event: PointerEvent) {
                    if (!view) {
                        return;
                    }
                    if (activePointerId !== undefined && event.pointerId !== activePointerId) {
                        return;
                    }

                    if (useRayMode && buildRayDir) {
                        buildRayDir(event.clientX, event.clientY, rayDirScratch);
                        const currentT = getRayLineClosestParam(
                            view.renderState.camera.position,
                            rayDirScratch,
                            effectiveAnchorPos,
                            normal,
                            rayLineScratch,
                        );
                        if (currentT === null) {
                            useRayMode = false;
                            buildRayDir = null;
                            vec3.scaleAndAdd(
                                computedAnchorScratch,
                                effectiveAnchorPos,
                                normal,
                                newOffset - effectiveNormalOffsetW,
                            );
                            const fallbackResult = initScreenSpaceDirection(
                                view,
                                computedAnchorScratch,
                                normal,
                                screenMoveDir,
                                refPosScratch,
                            );
                            if (!fallbackResult) {
                                return;
                            }
                            distanceBasedMult = fallbackResult.mult;
                            lastComputedOffset = newOffset;
                            vec2.set(lastPointerPos, event.clientX, event.clientY);
                            return;
                        }
                        const targetOffset = effectiveNormalOffsetW + (currentT - initialT);
                        const nextOffset = event.shiftKey ? newOffset + (targetOffset - newOffset) * 0.1 : targetOffset;
                        if (Math.abs(nextOffset - newOffset) >= 0.001) {
                            newOffset = nextOffset;
                            applyOffset(newOffset);
                        }
                        return;
                    }

                    if (!isFacingNormal && Math.abs(newOffset - lastComputedOffset) > 0.05) {
                        vec3.scaleAndAdd(
                            computedAnchorScratch,
                            effectiveAnchorPos,
                            normal,
                            newOffset - effectiveNormalOffsetW,
                        );
                        const result = initScreenSpaceDirection(
                            view,
                            computedAnchorScratch,
                            normal,
                            screenMoveDir,
                            refPosScratch,
                        );
                        if (result) {
                            distanceBasedMult = result.mult;
                            lastComputedOffset = newOffset;
                        } else {
                            vec2.set(pointerPos, event.clientX, event.clientY);
                            vec2.copy(lastPointerPos, pointerPos);
                            return;
                        }
                    }

                    const diffMult = distanceBasedMult * (event.shiftKey ? 0.1 : 1);
                    vec2.set(pointerPos, event.clientX, event.clientY);
                    vec2.sub(pointerDelta, lastPointerPos, pointerPos);
                    const step = vec2.dot(screenMoveDir, pointerDelta) * diffMult;

                    if (Math.abs(step) >= 0.001) {
                        newOffset += step;
                        applyOffset(newOffset);
                    }

                    vec2.copy(lastPointerPos, pointerPos);
                }

                window.addEventListener("keydown", onKeyDown);
                window.addEventListener("pointermove", onPointerMove);
                window.addEventListener("pointerup", onPointerUp);
                window.addEventListener("pointercancel", onPointerCancel);
                cleanupRef.current = () => stop(false);

                if (initialMoveEvent) {
                    onPointerMove(initialMoveEvent);
                }
            };

            if (e.pointerType === "touch") {
                const pointerId = e.pointerId;
                const startPos = vec2.fromValues(e.clientX, e.clientY);
                let settled = false;

                const stopPendingTouch = () => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    window.removeEventListener("pointermove", onPendingPointerMove);
                    window.removeEventListener("pointerup", onPendingPointerUp);
                    window.removeEventListener("pointercancel", onPendingPointerCancel);
                    if (cleanupRef.current === stopPendingTouch) {
                        cleanupRef.current = null;
                    }
                };

                const onPendingPointerMove = (event: PointerEvent) => {
                    if (event.pointerId !== pointerId || event.pointerType !== "touch") {
                        return;
                    }

                    const dx = event.clientX - startPos[0];
                    const dy = event.clientY - startPos[1];
                    if (dx * dx + dy * dy < TOUCH_DRAG_THRESHOLD_PX * TOUCH_DRAG_THRESHOLD_PX) {
                        return;
                    }

                    stopPendingTouch();
                    startDrag(e.clientX, e.clientY, event, pointerId);
                };

                const onPendingPointerUp = (event: PointerEvent) => {
                    if (event.pointerId !== pointerId || event.pointerType !== "touch") {
                        return;
                    }

                    stopPendingTouch();
                    if (onTapPlane) {
                        const tapCanvas = document.getElementById(ELEMENT_IDS.mainCanvas) as HTMLCanvasElement | null;
                        const canvasRect = tapCanvas?.getBoundingClientRect();
                        const tapPoint = canvasRect
                            ? vec2.fromValues(startPos[0] - canvasRect.left, startPos[1] - canvasRect.top)
                            : vec2.clone(startPos);
                        onTapPlane(index, tapPoint);
                    }
                };

                const onPendingPointerCancel = (event: PointerEvent) => {
                    if (event.pointerId !== pointerId || event.pointerType !== "touch") {
                        return;
                    }

                    stopPendingTouch();
                };

                window.addEventListener("pointermove", onPendingPointerMove);
                window.addEventListener("pointerup", onPendingPointerUp);
                window.addEventListener("pointercancel", onPendingPointerCancel);
                cleanupRef.current = stopPendingTouch;
                return;
            }

            // Mouse: same tap-vs-drag threshold as touch - call onTapPlane on clean click
            const mouseStartPos = vec2.fromValues(e.clientX, e.clientY);
            const mousePointerId = e.pointerId;
            let mouseSettled = false;

            const stopPendingMouse = () => {
                if (mouseSettled) {
                    return;
                }
                mouseSettled = true;
                window.removeEventListener("pointermove", onPendingMouseMove);
                window.removeEventListener("pointerup", onPendingMouseUp);
                window.removeEventListener("pointercancel", onPendingMouseCancel);
                if (cleanupRef.current === stopPendingMouse) {
                    cleanupRef.current = null;
                }
            };

            const onPendingMouseMove = (event: PointerEvent) => {
                if (event.pointerId !== mousePointerId) {
                    return;
                }
                const dx = event.clientX - mouseStartPos[0];
                const dy = event.clientY - mouseStartPos[1];
                if (dx * dx + dy * dy < MOUSE_DRAG_THRESHOLD_PX * MOUSE_DRAG_THRESHOLD_PX) {
                    return;
                }
                stopPendingMouse();
                startDrag(e.clientX, e.clientY, event, mousePointerId);
            };

            const onPendingMouseUp = (event: PointerEvent) => {
                if (event.pointerId !== mousePointerId) {
                    return;
                }
                stopPendingMouse();
                if (onTapPlane) {
                    const tapCanvas = document.getElementById(ELEMENT_IDS.mainCanvas) as HTMLCanvasElement | null;
                    const canvasRect = tapCanvas?.getBoundingClientRect();
                    const tapPoint = canvasRect
                        ? vec2.fromValues(mouseStartPos[0] - canvasRect.left, mouseStartPos[1] - canvasRect.top)
                        : vec2.clone(mouseStartPos);
                    onTapPlane(index, tapPoint);
                }
            };

            const onPendingMouseCancel = (event: PointerEvent) => {
                if (event.pointerId !== mousePointerId) {
                    return;
                }
                stopPendingMouse();
            };

            window.addEventListener("pointermove", onPendingMouseMove);
            window.addEventListener("pointerup", onPendingMouseUp);
            window.addEventListener("pointercancel", onPendingMouseCancel);
            cleanupRef.current = stopPendingMouse;
        },
        [actions, cameraType, clipping.planes, getDisplayAnchorPos, onDragStateChange, onTapPlane, undoRedo, view],
    );

    return { movingPlaneIndex, handlePointerDown };
}
